import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-900 mb-2">分类未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您访问的分类不存在</p>
          <Link
            to="/"
            className="inline-block px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-colors"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 91自拍论坛`,
    description: category.description,
    url: `https://www.91selfie.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '91自拍论坛',
      url: 'https://www.91selfie.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Header */}
          <section className="relative w-full h-[280px] md:h-[360px] flex items-center overflow-hidden">
            <img
              src={category.coverImage}
              alt={category.name}
              title={`${category.name} - 91自拍论坛`}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
            <div className="relative z-10 w-full px-4 md:px-6 max-w-7xl mx-auto pt-16">
              <div className="mb-4">
                <Breadcrumb items={[{ label: category.name }]} />
              </div>
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500/20 backdrop-blur-sm mb-4">
                <i className={`${category.icon} text-2xl text-background-50`}></i>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl text-background-50 mb-2">
                {category.name}
              </h1>
              <p className="text-sm md:text-base text-background-50/70 max-w-lg">
                {category.description}
              </p>
            </div>
          </section>

          {/* Article List */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-xl text-foreground-900">
                  共 {categoryArticles.length} 篇教程
                </h2>
              </div>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                    <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              )}

              {/* Category Navigation */}
              <div className="mt-16 pt-12 border-t border-background-200/70">
                <h3 className="font-heading text-lg text-foreground-900 mb-6 text-center">
                  探索更多分类
                </h3>
                <div className="flex flex-wrap justify-center gap-3">
                  {categories
                    .filter((c) => c.slug !== slug)
                    .map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="px-5 py-2.5 rounded-full border border-background-200/70 hover:border-primary-300 hover:bg-primary-50 text-sm text-foreground-600 hover:text-primary-600 whitespace-nowrap cursor-pointer transition-all duration-300"
                      >
                        {cat.name}
                        <span className="ml-1.5 text-xs text-foreground-400">({cat.articleCount})</span>
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}